/**
 * Status lines for the loading screen.
 *
 * These are written copy, not a readout. An earlier version named whatever the loader happened to
 * be fetching — accurate, and exactly the sort of thing that makes a game feel like a web page
 * downloading files. What a player wants during a long wait is a sense that something is being
 * built for them, and an acknowledgement that it is taking a moment.
 *
 * Order is deliberate: the immersive lines run first, and the reassuring ones only surface once
 * the load has genuinely dragged on, so "thanks for waiting" arrives when there is something to
 * thank someone for.
 */

/** Shown first — flavour, while the wait is still short. */
export const LOADING_MESSAGES_IMMERSIVE: readonly string[] = [
  'Summoning heroes…',
  'Gathering resources from across the kingdom…',
  'Preparing the battlefield…',
  'Awakening the ancient powers…',
  'The kingdom is coming to life…',
  'Forging your destiny…',
  'Preparing your armies for battle…',
  'The gates are opening…',
  'Your kingdom awaits. Just a little longer…',
]

/** Shown once the load has run long enough that patience is worth acknowledging. */
export const LOADING_MESSAGES_REASSURING: readonly string[] = [
  'Preparing your adventure… Please wait a moment.',
  'Loading the world… Almost there!',
  'Gathering resources… Thanks for your patience.',
  'Preparing everything for your journey…',
  'The world is taking shape. Please wait…',
  'Almost ready! Your adventure awaits.',
  'Loading a massive world takes a little time. Thanks for waiting!',
  'Just a moment… We’re getting everything ready for you.',
]

/**
 * How long each line holds before the next one.
 *
 * Paced for reading, not for looking busy. The longest lines here run past sixty characters, and
 * the earlier 3.8s hold cut several of them off mid-sentence for anyone not staring at the text —
 * on a loading screen the copy is the only thing to read, so it has to outlast a glance away.
 */
export const LOADING_MESSAGE_ROTATE_MS = 7000

/** Destination-appropriate opener, so a route loading screen starts on something that fits. */
export const AREA_FIRST_MESSAGE = {
  kingdom: 'The kingdom is coming to life…',
  'world-map': 'The world is taking shape. Please wait…',
  pvp: 'The gates are opening…',
  alliance: 'Gathering resources from across the kingdom…',
  pve: 'Preparing the battlefield…',
  mvm: 'Preparing your armies for battle…',
  minigame: 'Just a moment… We’re getting everything ready for you.',
} as const

function shuffled(list: readonly string[]): string[] {
  const out = [...list]
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[out[i], out[j]] = [out[j], out[i]]
  }
  return out
}

/**
 * Build the line-up for one loading screen: an optional opener, then the immersive lines in a
 * random order, then the reassuring ones. Shuffled so a player who reloads twice does not get the
 * identical script, which is what makes canned copy feel canned.
 */
export function buildLoadingMessageSequence(firstMessage?: string): string[] {
  const rest = [...shuffled(LOADING_MESSAGES_IMMERSIVE), ...shuffled(LOADING_MESSAGES_REASSURING)]
  if (!firstMessage) return rest
  return [firstMessage, ...rest.filter((m) => m !== firstMessage)]
}

/* ─────────────────────────── shared rotation across loading screens ─────────────────────────── */

/**
 * One script per loading episode, shared by every mounted loading screen.
 *
 * Cold boot and the route gates each render their own `GameLoadingScreen`, and during a handoff
 * both are mounted for a moment. With per-component timers that reads as the copy jumping — one
 * screen replaces the other mid-hold and the line changes seconds after the last one appeared,
 * which is what "the messages change too fast" actually looks like. Driving every screen from a
 * single sequence and a single timer means the script advances at one steady pace no matter how
 * many screens come and go.
 */
type MessageRotator = {
  sequence: string[]
  index: number
  timer: number | null
  subscribers: Set<(message: string) => void>
  /** When the last screen unmounted — used to decide whether the next one continues or restarts. */
  idleSince: number
}

/**
 * A gap longer than this means the previous load finished and this is a new wait, so the script
 * starts over (with its own opener). Shorter gaps are a handoff between two screens of the SAME
 * wait, which must continue where the copy left off.
 */
const EPISODE_RESET_AFTER_MS = 15_000

let rotator: MessageRotator | null = null

function stopRotatorTimer(r: MessageRotator): void {
  if (r.timer !== null) {
    window.clearInterval(r.timer)
    r.timer = null
  }
}

function startRotatorTimer(r: MessageRotator): void {
  if (r.timer !== null || r.sequence.length <= 1) return
  r.timer = window.setInterval(() => {
    // Holds on the last line rather than looping back: a player who has read the whole script is
    // deep into a long wait, and restarting it advertises that fact.
    if (r.index >= r.sequence.length - 1) {
      stopRotatorTimer(r)
      return
    }
    r.index += 1
    const message = r.sequence[r.index]
    r.subscribers.forEach((fn) => {
      try {
        fn(message)
      } catch {
        /* a broken subscriber must not stop the rotation */
      }
    })
  }, LOADING_MESSAGE_ROTATE_MS)
}

/**
 * The rotator for the current loading episode, starting one if the last screen has been gone long
 * enough to call this a new wait. Idempotent — safe to call from a `useState` initialiser.
 */
function ensureRotator(firstMessage: string | undefined): MessageRotator {
  const isNewEpisode =
    !rotator ||
    (rotator.subscribers.size === 0 && Date.now() - rotator.idleSince > EPISODE_RESET_AFTER_MS)

  if (isNewEpisode) {
    rotator = {
      sequence: buildLoadingMessageSequence(firstMessage),
      index: 0,
      timer: null,
      subscribers: new Set(),
      idleSince: Date.now(),
    }
  }
  return rotator as MessageRotator
}

/** The line to show right now, without subscribing — for a screen's first render. */
export function peekLoadingMessage(firstMessage?: string): string {
  const r = ensureRotator(firstMessage)
  return r.sequence[r.index] ?? r.sequence[0] ?? ''
}

/**
 * Subscribe a loading screen to the shared script. Returns the line to show right now plus an
 * unsubscribe. `firstMessage` only applies when a new episode starts — mid-episode it is ignored,
 * because restarting the copy for a handoff is the very thing this exists to prevent.
 */
export function subscribeLoadingMessage(
  firstMessage: string | undefined,
  onMessage: (message: string) => void,
): { current: string; unsubscribe: () => void } {
  const r = ensureRotator(firstMessage)
  r.subscribers.add(onMessage)
  startRotatorTimer(r)

  return {
    current: r.sequence[r.index] ?? r.sequence[0] ?? '',
    unsubscribe: () => {
      r.subscribers.delete(onMessage)
      if (r.subscribers.size === 0) {
        // Freeze the script while nothing is on screen: a wait that resumes should pick the line
        // up where the player last saw it, not several lines further on.
        stopRotatorTimer(r)
        r.idleSince = Date.now()
      }
    },
  }
}
